import { api } from "@/lib/apiClient";
import type { HeroProfile } from "@/lib/auth";
import { currentYear, yearDateRange } from "@/lib/auswertungen";
import { devSimulatedBirthDateToday } from "@/lib/birthday";
import { fetchInvoiceRevenueReport } from "@/lib/heroDocumentReports";
import { fetchHoursPipelineReport } from "@/lib/hoursPipeline";
import { notifyMentionsBadgeRefresh } from "@/lib/navBadges";
import { listTimesheetsSummary, type TimesheetSummary } from "@/lib/timesheets";
import {
  hasOpenBillingQuestions,
  showsAnsweredBillingQuestionBadge,
} from "@/pages/stundennachweise/timesheetFilterLogic";

export const HERO_PROJECT_BASE = "https://login.hero-software.de/partner/Projects/view/";
export const NEWS_PREVIEW_LIMIT = 3;
export const MENTIONS_PREVIEW_LIMIT = 3;

export interface HomeOrderRow {
  id?: number;
  auftragsnummer?: string | null;
  ordnernummer?: string | null;
  beschreibung?: string | null;
  ort_name?: string | null;
  ort?: string | null;
  hero_project_id?: string | number | null;
}

export interface HomeNewsItem {
  id: number;
  title?: string | null;
  body?: string | null;
  created_at?: string | null;
  created_by?: string | null;
  created_by_name?: string | null;
  is_read?: boolean;
}

export interface MentionReference {
  type?: string;
  ref_id?: string | number;
  label?: string | null;
  token_text?: string | null;
  start?: number | null;
  end?: number | null;
}

export interface HomeMention {
  id: number;
  note_id: number;
  auftrag_id?: number | null;
  auftragsnummer?: string | null;
  note_text?: string | null;
  note_preview?: string | null;
  note_author?: string | null;
  note_author_login?: string | null;
  note_created_at?: string | null;
  note_references?: MentionReference[];
  read_at?: string | null;
}

export interface HeroEmployeeBirthday {
  user_id?: number | string | null;
  full_name?: string | null;
  name?: string | null;
  birth_date?: string | null;
}

export type {
  ProjectCompletionRow,
  ProjectCompletionStatusKey,
} from "@/lib/projectCompletions";
export {
  fetchProjectCompletions,
  filterOpenProjectCompletions,
  getProjectCompletionStatusMeta,
} from "@/lib/projectCompletions";

export async function fetchLatestOrders(limit = 5): Promise<HomeOrderRow[]> {
  const data = await api.get<HomeOrderRow[] | { items?: HomeOrderRow[] }>(
    `/auftraege/latest?limit=${limit}`,
  );
  return Array.isArray(data) ? data : data.items ?? [];
}

export async function fetchNews(): Promise<HomeNewsItem[]> {
  const all = await api.get<HomeNewsItem[]>("/news");
  const rows = Array.isArray(all) ? all : [];
  return [...rows].sort((a, b) => {
    const ta = new Date(a?.created_at || 0).getTime();
    const tb = new Date(b?.created_at || 0).getTime();
    return tb - ta;
  });
}

export async function markNewsRead(id: number): Promise<void> {
  await api.post(`/news/${id}/read`);
}

export async function fetchUnreadMentions(limit = 50): Promise<HomeMention[]> {
  const data = await api.get<HomeMention[]>(
    `/mentions/me?only_unread=true&limit=${limit}`,
  );
  return Array.isArray(data) ? data : [];
}

export async function fetchReadMentions(limit = 50): Promise<HomeMention[]> {
  const data = await api.get<HomeMention[]>(
    `/mentions/me?only_unread=false&only_read=true&limit=${limit}`,
  );
  return (Array.isArray(data) ? data : []).filter((m) => Boolean(m.read_at));
}

export async function markMentionRead(id: number): Promise<void> {
  await api.post(`/mentions/${id}/read`);
  notifyMentionsBadgeRefresh();
}

export async function fetchTodaysBirthdays(
  me: HeroProfile | null,
  options?: { simulateBirthday?: boolean; displayName?: string | null },
): Promise<{ me: HeroProfile | null; todays: HeroEmployeeBirthday[] }> {
  let effectiveMe = me;
  if (options?.simulateBirthday) {
    effectiveMe = {
      ...(me || {}),
      full_name: me?.full_name || options.displayName || null,
      dev_simulate_birthday: true,
      birth_date: me?.birth_date || devSimulatedBirthDateToday(),
    };
  }

  const data = await api.get<HeroEmployeeBirthday[]>("/hero/employees/birthdays");
  const todays = Array.isArray(data) ? [...data] : [];
  if (effectiveMe?.dev_simulate_birthday) {
    const hasSelf = todays.some(
      (p) =>
        (effectiveMe!.hero_user_id != null && p.user_id === effectiveMe!.hero_user_id) ||
        (effectiveMe!.full_name &&
          (p.full_name === effectiveMe!.full_name || p.name === effectiveMe!.full_name)),
    );
    if (!hasSelf) {
      todays.unshift({
        user_id: effectiveMe.hero_user_id,
        full_name: effectiveMe.full_name,
        birth_date: effectiveMe.birth_date,
      });
    }
  }
  return { me: effectiveMe, todays };
}

/** Stundennachweise mit offenen Abrechnungs-Rückfragen (Geschäftsführung). */
export async function fetchTimesheetsWithOpenBillingQuestions(): Promise<TimesheetSummary[]> {
  const all = await listTimesheetsSummary();
  return all.filter((ts) => !ts.has_newer_revision && hasOpenBillingQuestions(ts));
}

/**
 * Stundennachweise, bei denen alle Rückfragen beantwortet sind.
 * Sortierung: zuletzt beantwortete Rückfrage zuerst.
 */
export async function fetchTimesheetsWithAnsweredBillingQuestions(): Promise<TimesheetSummary[]> {
  const all = await listTimesheetsSummary();
  return all
    .filter((ts) => !ts.has_newer_revision && showsAnsweredBillingQuestionBadge(ts))
    .sort((a, b) => {
      const aAt = Date.parse(String(a.billing_last_answered_at || "")) || 0;
      const bAt = Date.parse(String(b.billing_last_answered_at || "")) || 0;
      if (bAt !== aAt) return bAt - aAt;
      return Number(b.id || 0) - Number(a.id || 0);
    });
}

export function getOpenBillingQuestionCount(ts: TimesheetSummary): number {
  const openCount = Number(ts.billing_question_open_count);
  if (Number.isFinite(openCount) && openCount > 0) return openCount;
  return hasOpenBillingQuestions(ts) ? 1 : 0;
}

/** Summe aller offenen Rückfrage-Teile (wie auf der Home-Seite). */
export async function fetchOpenBillingQuestionTotalCount(): Promise<number> {
  const rows = await fetchTimesheetsWithOpenBillingQuestions();
  return rows.reduce((sum, ts) => sum + getOpenBillingQuestionCount(ts), 0);
}

/**
 * Geschäftsführungs-KPIs aus denselben Auswertungs-Quellen:
 * - Rechnungswert = Gesamtumsatz (Hero + App) aus Umsatz & Zahlungen
 * - Abgerechnete Stunden = invoice_created + invoice_paid aus Stunden-Pipeline
 */
export async function fetchManagementTimesheetMetrics(): Promise<{
  billedHours: number;
  invoiceValue: number;
}> {
  const year = currentYear();
  const { from, to } = yearDateRange(year);
  const [revenue, pipeline] = await Promise.all([
    fetchInvoiceRevenueReport(year),
    fetchHoursPipelineReport(from, to),
  ]);

  const billedHours =
    Number(pipeline.summary?.invoice_created || 0) +
    Number(pipeline.summary?.invoice_paid || 0);
  const invoiceValue = Number(revenue.summary?.total_amount || 0);

  return { billedHours, invoiceValue };
}

export function formatHoursMetric(value: number): string {
  return `${(Number(value) || 0).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} h`;
}

export function formatCurrencyMetric(value: number): string {
  return (Number(value) || 0).toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function toCompactPreview(text: string, maxLength = 160): string {
  const normalized = String(text || "").replace(/\s+/g, " ").trim();
  if (!normalized) return "Ohne Inhalt";
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 1)}…`;
}
